/* Royal viewport scheduler: keep server HTML; prepare optional work near the screen. */
(() => {
  if (window.RoyalViewport) return;
  const design = !!window.Shopify?.designMode;
  const supported = 'IntersectionObserver' in window && !design;
  const jobs = new WeakMap();
  const pendingTasks = new Set();
  const seenImages = new WeakSet();
  const pendingImages = new Set();
  const seenCarousels = new WeakSet();
  const near = (el, margin = 150) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom >= -margin && r.top <= innerHeight + margin && r.right >= 0 && r.left <= innerWidth;
  };
  const observers = new Map();
  function observerFor(margin) {
    if (!observers.has(margin)) {
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          const task = jobs.get(entry.target); jobs.delete(entry.target); pendingTasks.delete(entry.target);
          entry.target.removeAttribute('data-royal-pending');
          if (entry.target.isConnected) task?.();
        }
      }, { rootMargin: `${margin}px 0px` });
      observers.set(margin, observer);
    }
    return observers.get(margin);
  }
  function once(el, task, margin = 150) {
    if (!el) return;
    if (!supported || near(el, margin)) { task(); return; }
    if (jobs.has(el)) return;
    jobs.set(el, task); pendingTasks.add(el); el.setAttribute('data-royal-pending', '');
    observerFor(margin).observe(el);
  }
  function loadImage(img) {
    const src = img.getAttribute('data-royal-src');
    if (!src) return;
    img.loading = 'eager';
    const srcset = img.getAttribute('data-royal-srcset');
    if (srcset) img.setAttribute('srcset', srcset);
    img.setAttribute('src', src);
    img.removeAttribute('data-royal-src');
    img.removeAttribute('data-royal-srcset');
    img.setAttribute('data-royal-image-ready', '');
    img.addEventListener('load', () => img.classList.add('lazyloadbeeed'), { once: true });
    imageObserver?.unobserve(img);
    pendingImages.delete(img);
  }
  const connection = navigator.connection;
  let imageObserver = null, imageMargin = '';
  function imagePolicy(direction = 1, speed = 0) {
    if (!supported) return;
    const constrained = connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '');
    const mobile = matchMedia('(max-width: 767px)').matches;
    const ahead = constrained ? 100 : speed > 1.5 ? (mobile ? 700 : 1000) : (mobile ? 350 : 500);
    const behind = constrained ? 50 : 150;
    const margin = direction < 0 ? `${ahead}px 100px ${behind}px 100px` : `${behind}px 100px ${ahead}px 100px`;
    for (const img of pendingImages) {
      if (!img.isConnected) { imageObserver?.unobserve(img); pendingImages.delete(img); }
    }
    if (margin === imageMargin) return;
    imageMargin = margin;
    imageObserver?.disconnect();
    imageObserver = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) loadImage(entry.target);
    }, { rootMargin: margin });
    pendingImages.forEach(img => imageObserver.observe(img));
  }
  imagePolicy();
  if (supported) {
    let lastY = scrollY, lastTime = performance.now(), lastPolicy = 0, frame = 0;
    addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(now => {
        frame = 0;
        const y = scrollY, distance = y - lastY;
        const speed = Math.abs(distance) / Math.min(100, Math.max(16, now - lastTime));
        lastY = y; lastTime = now;
        if (now - lastPolicy < 200 || !distance) return;
        lastPolicy = now;
        imagePolicy(Math.sign(distance), speed);
      });
    }, { passive: true });
    connection?.addEventListener('change', () => imagePolicy());
  }
  function images(scope = document) {
    for (const el of pendingTasks) {
      if (el.isConnected) continue;
      observers.forEach(observer => observer.unobserve(el));
      jobs.delete(el); pendingTasks.delete(el);
    }
    const list = [...scope.querySelectorAll('img[data-royal-src]')];
    if (scope.matches?.('img[data-royal-src]')) list.unshift(scope);
    list.forEach(img => {
      if (seenImages.has(img)) return;
      seenImages.add(img);
      if (!supported) loadImage(img);
      else { pendingImages.add(img); imageObserver.observe(img); }
    });
  }
  function carousel(el) {
    if (seenCarousels.has(el)) return;
    const instance = el.flickitybee?.flkty;
    if (!instance) return;
    seenCarousels.add(el);
    const prepareMedia = () => {
      if (!near(el, 250)) return;
      const cells = instance.cells || [];
      const index = instance.selectedIndex || 0;
      [cells[index], cells[(index + 1) % cells.length]].forEach(cell => {
        cell?.element.querySelectorAll('img[data-royal-src]').forEach(loadImage);
      });
    };
    if (el.hasAttribute('data-main-media')) {
      once(el, prepareMedia);
      instance.on('select', prepareMedia);
    }
    if (supported && instance.options?.autoPlay) {
      let pausedHere = false;
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting && instance.player?.state === 'playing') {
          instance.pausePlayer(); pausedHere = true;
        } else if (entry.isIntersecting && pausedHere) {
          if (instance.player?.state === 'paused') instance.unpausePlayer();
          pausedHere = false;
        }
      });
      observer.observe(el);
    }
  }
  // Optional drawers need their data only when approached or opened.
  // Capture intent before Nitro's click handler, and also support programmatic opens.
  function drawer(id, task) {
    const panel = document.getElementById(id);
    if (!panel) return;
    if (design) { task(); return; }
    const selector = '[data-drawer-options]';
    const matches = el => {
      const trigger = el?.closest?.(selector);
      if (!trigger) return false;
      try { return JSON.parse(trigger.getAttribute('data-drawer-options')).id === '#' + id; }
      catch { return false; }
    };
    let started = false, observer;
    const events = ['pointerover', 'pointerdown', 'focusin', 'click'];
    function start() {
      if (started) return;
      started = true;
      events.forEach(type => document.removeEventListener(type, intent, true));
      observer?.disconnect();
      const css = document.querySelector('[data-royal-drawer-style="' + id + '"]');
      if (css && !css.href) css.href = css.dataset.royalHref;
      task();
    }
    function intent(event) { if (matches(event.target)) start(); }
    events.forEach(type => document.addEventListener(type, intent, true));
    observer = new MutationObserver(() => {
      if (panel.getAttribute('aria-hidden') === 'false') start();
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['aria-hidden'] });
    if (panel.getAttribute('aria-hidden') === 'false' || matches(document.activeElement) ||
        [...document.querySelectorAll(selector)].some(el => el.matches(':hover') && matches(el))) start();
  }
  window.RoyalViewport = { once, images, carousel, drawer };
  images();
  // Discover parser-added images without waiting for the large deferred theme bundles.
  if (document.readyState === 'loading') {
    const parserObserver = new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1) images(node);
      }));
    });
    parserObserver.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('DOMContentLoaded', () => {
      parserObserver.disconnect(); images();
    }, { once: true });
  }
  document.addEventListener('shopify:section:load', event => { images(event.target); });
})();
